/**
 * Passive side-channel for glyph builders: records repeated sub-glyph
 * (module) ranges and true-arc metadata alongside the flat segment array,
 * keyed on the array instance itself. Rendering ignores it entirely; the DXF
 * export reads it to emit shared block definitions and bulged arc polylines
 * instead of segment soup.
 */

import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import type { SketchObjectFrame } from '../types';

/** A repeated sub-glyph: segments [segStart, segEnd) drawn in `frame`. */
export interface GlyphInstanceRecord {
  /** Geometry identity within one build — equal keys ⇒ identical module geometry. */
  key: string;
  frame: SketchObjectFrame;
  segStart: number;
  segEnd: number;
}

/** A tessellated arc: chords [segStart, segEnd) approximating one true arc. */
export interface GlyphArcRecord {
  segStart: number;
  segEnd: number;
  /** Arc center in the build's output space (same space as the segments). */
  center: Pt2;
  radius: number;
  /**
   * Signed sweep (a1 − a0) in the build frame's u/n angle space. On mirrored
   * (left-handed) frames the planar direction flips — derive the drawing-plane
   * direction from the chord geometry, not this sign.
   */
  sweep: number;
}

interface GlyphMeta {
  openInstance: { key: string; frame: SketchObjectFrame; segStart: number } | null;
  instances: GlyphInstanceRecord[];
  arcs: GlyphArcRecord[];
}

const metaByArray = new WeakMap<number[], GlyphMeta>();

function metaFor(out: number[]): GlyphMeta {
  let meta = metaByArray.get(out);
  if (!meta) {
    meta = { openInstance: null, instances: [], arcs: [] };
    metaByArray.set(out, meta);
  }
  return meta;
}

/** Mark the start of a repeated sub-glyph. Nested begins are ignored. */
export function beginGlyphInstance(out: number[], key: string, frame: SketchObjectFrame): void {
  const meta = metaFor(out);
  if (meta.openInstance) return;
  meta.openInstance = { key, frame, segStart: out.length };
}

export function endGlyphInstance(out: number[]): void {
  const meta = metaFor(out);
  if (!meta.openInstance) return;
  const { key, frame, segStart } = meta.openInstance;
  meta.openInstance = null;
  if (out.length > segStart) meta.instances.push({ key, frame, segStart, segEnd: out.length });
}

export function recordGlyphArc(out: number[], center: Pt2, radius: number, sweep: number, segStart: number): void {
  metaFor(out).arcs.push({ segStart, segEnd: out.length, center, radius, sweep });
}

export function getGlyphInstances(out: readonly number[]): readonly GlyphInstanceRecord[] {
  return metaByArray.get(out as number[])?.instances ?? [];
}

export function getGlyphArcs(out: readonly number[]): readonly GlyphArcRecord[] {
  return metaByArray.get(out as number[])?.arcs ?? [];
}
